import * as THREE from "three";
import {TrackMap} from "../track/map/trackMap.js";

/**
 * A racer pointer
 */
export class Pointer {
    static CLASS = "pointer";
    static CLASS_HIDDEN = "hidden";
    static CLASS_SMALL = "small";
    static CLASS_ARROW = "arrow";
    static CLASS_NAME_TAG = "name-tag";
    static CLASS_NAME_PILOT_SHELL = "pilot-shell";
    static ARROW_RAISE = 22;
    static ARROW_WIDTH = 19;
    static ARROW_HEIGHT = 18;
    static ARROW_DENT = 4;
    static HIDE_DISTANCE = 10;
    static RAISE = 2;

    /**
     * Create the arrow
     * @param {THREE.Color} color The arrow color
     * @returns {SVGSVGElement} The arrow element
     */
    static createArrow(color) {
        const svg = document.createElementNS(TrackMap.SVG_URI, "svg");
        const left = document.createElementNS(TrackMap.SVG_URI, "path");
        const right = document.createElementNS(TrackMap.SVG_URI, "path");

        left.setAttribute("class", Pointer.CLASS_ARROW);
        left.setAttribute("fill", color.getStyle());
        left.setAttributeNS(null, "d",
            "M 0 " + (-Pointer.ARROW_RAISE) +
            "L " + (Pointer.ARROW_WIDTH * -.5) + " " + (-Pointer.ARROW_HEIGHT - Pointer.ARROW_RAISE) +
            "L 0 " + (-Pointer.ARROW_HEIGHT - Pointer.ARROW_RAISE + Pointer.ARROW_DENT) +
            "L " + (Pointer.ARROW_WIDTH * .5) + " " + (-Pointer.ARROW_HEIGHT - Pointer.ARROW_RAISE) +
            "Z");

        right.setAttribute("class", Pointer.CLASS_ARROW);
        right.setAttribute("opacity", "0.3");
        right.setAttribute("fill", "black");
        right.setAttributeNS(null, "d",
            "M 0 " + (-Pointer.ARROW_RAISE) +
            "L " + (Pointer.ARROW_WIDTH * .5) + " " + (-Pointer.ARROW_HEIGHT - Pointer.ARROW_RAISE) +
            "L 0 " + (-Pointer.ARROW_HEIGHT - Pointer.ARROW_RAISE + Pointer.ARROW_DENT) +
            "Z");

        svg.appendChild(left);
        svg.appendChild(right);

        return svg;
    }

    /**
     * Create the name tag element
     * @param {string} name The name
     * @param {string} avatar The URL to the avatar image
     * @returns {HTMLDivElement} The name tag element
     */
    static createNameTag(name, avatar) {
        const element = document.createElement("div");
        const pilotshell = document.createElement("div");
        const imageshell = document.createElement("div");
        const textshell = document.createElement("div");

        // Setup Pilot Tag Image
        let tmpimg = avatar;
        tmpimg = 'https://exr.mypinata.cloud/ipfs/Qmey7boQhHUYTg3a7d1M67Uin3Jk8cfLA92PhmKvnF8SG2';
        let imghtml = '<div class="pointer-img-full" style="background: url(\''+tmpimg+'\');background-size:cover"></div>';
        imageshell.insertAdjacentHTML('beforeend',imghtml);

        // Setup Pilot Tag Stats
        let statshtml = '<div class="row" style="border-top:1px solid white;padding-top:10px;margin-top:2px;margin-bottom:5px">';
        statshtml = statshtml+'<div class="col s8">Win %:</div><div class="col s4 right-align">80</div></div>';
        statshtml = statshtml+'<div class="row" style="margin-bottom:5px"><div class="col s8">Speed:</div><div class="col s4 right-align">65</div></div>';
        statshtml = statshtml+'<div class="row" style="margin-bottom:5px"><div class="col s8">Focus:</div><div class="col s4 right-align">62</div></div>';
        statshtml = statshtml+'<div class="row" style="margin-bottom:5px"><div class="col s8">Accel:</div><div class="col s4 right-align">60</div></div>';
        textshell.insertAdjacentHTML('beforeend',statshtml);

        pilotshell.className = Pointer.CLASS_NAME_PILOT_SHELL;
        pilotshell.appendChild(imageshell);
        pilotshell.appendChild(textshell);

        element.className = Pointer.CLASS_NAME_TAG;

        element.appendChild(document.createTextNode(name));
        element.appendChild(pilotshell);
        

        return element;
    }

    /**
     * Construct a racer pointer
     * @param {Racer} racer The racer to follow
     */
    constructor(racer) {
        this.racer = racer;
        this.element = document.createElement("div");
        this.element.className = Pointer.CLASS;
        this.screenPosition = new THREE.Vector3();
        this.hidden = false;
        this.small = false;

        this.element.appendChild(Pointer.createArrow(racer.data.color));
        this.element.appendChild(Pointer.createNameTag(racer.data.racerName, racer.data.urlAvatar));

        this.setSmall(true);
    }

    /**
     * Set whether this pointer is small or expanded
     * @param {boolean} small True if the pointer must be small
     */
    setSmall(small) {
        if (this.small = small) {
            this.element.classList.add(Pointer.CLASS_SMALL);
            this.element.getElementsByClassName(Pointer.CLASS_NAME_PILOT_SHELL)[0].style.display = 'none';
        } else {
            this.element.classList.remove(Pointer.CLASS_SMALL);
            this.element.getElementsByClassName(Pointer.CLASS_NAME_PILOT_SHELL)[0].style.display = 'block';
        }
    }

    /**
     * Update before rendering
     * @param {number} time The time interpolation in the range [0, 1]
     * @param {Camera} camera The camera
     * @param {number} width The screen width in pixels
     * @param {number} height The screen height in pixels
     * @param {number} index The sorted index
     */
    render(time, camera, width, height, index) {
        if (!this.racer.ship)
            return;

        let hide = false;

        if (this.small) {
            const cameraDistance = this.racer.ship.position.distanceTo(camera.position);

            hide = cameraDistance < Pointer.HIDE_DISTANCE;
        }

        if (!hide) {
            this.screenPosition.setFromMatrixPosition(this.racer.ship.matrixWorld);
            this.screenPosition.y += Pointer.RAISE;
            this.screenPosition.project(camera);

            if (this.screenPosition.z > 1)
                hide = true;
        }

        if (hide) {
            if (!this.hidden) {
                this.hidden = true;
                this.element.classList.add(Pointer.CLASS_HIDDEN);
            }
        }
        else if (this.hidden) {
            this.hidden = false;
            this.element.classList.remove(Pointer.CLASS_HIDDEN);
        }

        this.element.style.zIndex = index.toString();
        this.element.style.transform = "translate(" +
            ((this.screenPosition.x + 1) * .5 * width).toFixed(2) + "px," +
            ((1 - this.screenPosition.y) * .5 * height).toFixed(2) + "px)";
    }
}